import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import identity from 'lodash/identity';
import isFunction from 'lodash/isFunction';

// HELPERS
import { useListData } from 'helpers/getListData';

// LOCAL COMPONENTS
import ListCardCollapse from './ListCardCollapse';
import LinkCell from '../../tools/LinkCell';

// STYLES
import styles from './sortedGroup.module.scss';

// MAIN COMPONENT
const SortedGroup = forwardRef(
  (
    {
      index, //
      mobilePreviewKeys,
      result,
      setRowHeight,
      style: styleProp = {},
      titleFormat = identity,
      toolbar,
      ...props
    },
    ref
  ) => {
    const { title, data } = result[index] || {};

    // STATE
    const [resizeRowTrigger, setResizeRowTrigger] = useState(false);

    // REFS
    const groupRef = useRef({});

    // LIST CONTEXT
    const {
      columns: rawColumns = [], //
      preferences: { showColumns } = {}
    } = {...(useListData() || {}), ...props};

    // MEMOS
    const columns = useMemo(() => {
      // Filter out any hidden columns
      const shown = showColumns ? rawColumns.filter(c => showColumns.includes(c.key)) : rawColumns;
      return shown.map(c => {
        if (c.to) c.component = LinkCell;
        return c;
      });
    }, [rawColumns, showColumns]);

    const style = useMemo(() => {
      const style = { ...styleProp };
      if (setRowHeight) style.height = 'auto';
      return style;
    }, [setRowHeight, styleProp]);

    // EFFECTS
    useEffect(() => {
      if (groupRef?.current && setRowHeight) {
        setRowHeight(index, groupRef.current.clientHeight, ref);
      }
    }, [index, ref, setRowHeight, resizeRowTrigger]);

    // CALLBACKS
    const handleResizeRow = useCallback(() => {
      setTimeout(() => {
        setResizeRowTrigger(!resizeRowTrigger);
      }, 0);
    }, [resizeRowTrigger]);

    // RENDER
    return (
      <div className={styles.container} ref={groupRef} style={style}>
        <div className={styles.header}>
          <h3 className={styles.title}>{titleFormat(title)}</h3>
          <div className={styles.toolbar}>{isFunction(toolbar) ? toolbar(data) : toolbar}</div>
        </div>
        <div className="flex-grow-1 d-flex flex-column mb-0">
          <div className={clsx(styles.body, 'tbody flex-grow-1')}>
            {data.map((row, index) => (
              <ListCardCollapse
                {...props}
                columns={columns}
                index={index}
                key={index}
                previewKeys={mobilePreviewKeys}
                resizeRow={handleResizeRow}
                row={row}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
);

// EXPORT
export default SortedGroup;
